import type { AxiosInstance, AxiosRequestConfig } from "axios";

const MAX_HYDRA_PAGES = 1_000;

export type HydraCollectionParams = Record<string, boolean | number | string | undefined>;

type HydraCollectionData = {
    "hydra:member"?: unknown;
    "hydra:totalItems"?: unknown;
    "hydra:view"?: unknown;
};

type FetchAllHydraMembersOptions = {
    client: Pick<AxiosInstance, "defaults" | "get">;
    endpoint: string;
    itemsPerPage?: number;
    params?: HydraCollectionParams;
    requestConfig?: Omit<AxiosRequestConfig, "params">;
};

const asCollection = (value: unknown): HydraCollectionData =>
    typeof value === "object" && value !== null ? (value as HydraCollectionData) : {};

const readMembers = (data: HydraCollectionData, endpoint: string): unknown[] => {
    const members = data["hydra:member"];
    if (!Array.isArray(members)) {
        throw new Error(`Invalid Hydra collection returned by ${endpoint}: missing hydra:member array`);
    }
    return members;
};

const readTotal = (data: HydraCollectionData, endpoint: string): number | undefined => {
    const total = data["hydra:totalItems"];
    if (total === undefined) return undefined;
    if (typeof total !== "number" || !Number.isSafeInteger(total) || total < 0) {
        throw new Error(`Invalid hydra:totalItems received for ${endpoint}`);
    }
    return total;
};

const readNext = (data: HydraCollectionData, endpoint: string): string | undefined => {
    const view = data["hydra:view"];
    if (view === undefined) return undefined;
    if (typeof view !== "object" || view === null || Array.isArray(view)) {
        throw new Error(`Invalid hydra:view received for ${endpoint}`);
    }
    const next = (view as Record<string, unknown>)["hydra:next"];
    if (next === undefined) return undefined;
    if (typeof next !== "string" || next.length === 0) {
        throw new Error(`Invalid hydra:next received for ${endpoint}`);
    }
    return next;
};

const normalizeNext = (
    client: Pick<AxiosInstance, "defaults">,
    endpoint: string,
    next: string,
    params: HydraCollectionParams,
    expectedPage: number,
): string => {
    const fallbackBaseUrl = "https://hydra.local";
    let endpointUrl: URL;
    let nextUrl: URL;

    try {
        const apiBaseUrl = new URL(client.defaults.baseURL ?? "/", fallbackBaseUrl);
        endpointUrl = new URL(endpoint, apiBaseUrl);
        nextUrl = new URL(next, endpointUrl);
    } catch {
        throw new Error(`Invalid Hydra next link received for ${endpoint}`);
    }

    if (nextUrl.origin !== endpointUrl.origin) {
        throw new Error(`Hydra next link changes collection origin for ${endpoint}`);
    }
    if (nextUrl.pathname !== endpointUrl.pathname) {
        throw new Error(`Hydra next link changes collection pathname for ${endpoint}`);
    }

    const expectedParams = { ...params, page: expectedPage };
    const expectedKeys = new Set(
        Object.entries(expectedParams)
            .filter(([, value]) => value !== undefined)
            .map(([key]) => key),
    );
    let hasUnexpectedParameter = false;
    nextUrl.searchParams.forEach((_value, key) => {
        if (!expectedKeys.has(key)) hasUnexpectedParameter = true;
    });
    const hasChangedExpectedValue = Object.entries(expectedParams).some(([key, value]) => {
        if (value === undefined) return false;
        const values = nextUrl.searchParams.getAll(key);
        return values.length !== 1 || values[0] !== String(value);
    });

    if (nextUrl.hash !== "" || hasUnexpectedParameter || hasChangedExpectedValue) {
        throw new Error(`Hydra next link changes collection query scope for ${endpoint}`);
    }

    return nextUrl.pathname + nextUrl.search;
};

const reconcileTotal = (
    data: HydraCollectionData,
    expectedTotal: number | undefined,
    endpoint: string,
): number | undefined => {
    const pageTotal = readTotal(data, endpoint);
    if (expectedTotal !== undefined && pageTotal !== undefined && pageTotal !== expectedTotal) {
        throw new Error(
            `Inconsistent hydra:totalItems for ${endpoint}: expected ${expectedTotal}, received ${pageTotal}`,
        );
    }
    return expectedTotal ?? pageTotal;
};

const memberIdentity = (member: unknown, endpoint: string): string => {
    if (typeof member !== "object" || member === null) {
        throw new Error(`Invalid Hydra collection returned by ${endpoint}: member has no stable identifier`);
    }
    if ("id" in member && (typeof member.id === "number" || typeof member.id === "string")) {
        return String(member.id);
    }
    if ("@id" in member && typeof member["@id"] === "string" && member["@id"].length > 0) {
        return member["@id"];
    }
    throw new Error(`Invalid Hydra collection returned by ${endpoint}: member has no stable identifier`);
};

const assertUniqueMembers = (members: unknown[], endpoint: string) => {
    const seen = new Set<string>();
    for (const member of members) {
        const identity = memberIdentity(member, endpoint);
        if (seen.has(identity)) {
            throw new Error(`Invalid Hydra collection returned by ${endpoint}: duplicate member ${identity}`);
        }
        seen.add(identity);
    }
};

const cardinalityError = (endpoint: string, total: number, received: number) =>
    new Error(`Hydra pagination cardinality mismatch for ${endpoint}: expected ${total}, received ${received}`);

export const fetchAllHydraMembers = async ({
    client,
    endpoint,
    itemsPerPage = 100,
    params = {},
    requestConfig,
}: FetchAllHydraMembersOptions): Promise<unknown[]> => {
    const collectionParams = { ...params, itemsPerPage };
    const firstPageConfig: AxiosRequestConfig = {
        ...requestConfig,
        params: { ...collectionParams, page: 1 },
    };
    const firstResponse = await client.get(endpoint, firstPageConfig);
    const firstData = asCollection(firstResponse.data);
    const allMembers = [...readMembers(firstData, endpoint)];
    let total = readTotal(firstData, endpoint);
    let next = readNext(firstData, endpoint);
    let pagesFetched = 1;
    const visitedLinks = new Set<string>();

    if (total !== undefined && allMembers.length > total) {
        throw cardinalityError(endpoint, total, allMembers.length);
    }
    if (total === undefined && next === undefined && allMembers.length > 0) {
        throw new Error(`Hydra collection returned by ${endpoint} is missing pagination metadata`);
    }

    const getNextPage = async (url: string) =>
        requestConfig === undefined ? await client.get(url) : await client.get(url, requestConfig);

    while (next !== undefined) {
        if (pagesFetched >= MAX_HYDRA_PAGES) {
            throw new Error(`Hydra pagination exceeded safe page limit for ${endpoint}`);
        }
        const normalizedNext = normalizeNext(client, endpoint, next, collectionParams, pagesFetched + 1);
        if (visitedLinks.has(normalizedNext)) {
            throw new Error(`Cyclic Hydra pagination link received for ${endpoint}`);
        }
        visitedLinks.add(normalizedNext);

        const response = await getNextPage(normalizedNext);
        const data = asCollection(response.data);
        total = reconcileTotal(data, total, endpoint);
        const pageMembers = readMembers(data, endpoint);
        if (pageMembers.length === 0) {
            throw new Error(`Hydra pagination made no member progress for ${endpoint}`);
        }
        allMembers.push(...pageMembers);
        pagesFetched += 1;
        next = readNext(data, endpoint);

        if (total !== undefined && allMembers.length > total) {
            throw cardinalityError(endpoint, total, allMembers.length);
        }
    }

    if (total !== undefined) {
        let page = pagesFetched + 1;
        while (allMembers.length < total) {
            if (pagesFetched >= MAX_HYDRA_PAGES) {
                throw new Error(`Hydra pagination exceeded safe page limit for ${endpoint}`);
            }
            const pageConfig: AxiosRequestConfig = {
                ...requestConfig,
                params: { ...collectionParams, page },
            };
            const response = await client.get(endpoint, pageConfig);
            const data = asCollection(response.data);
            reconcileTotal(data, total, endpoint);
            const pageMembers = readMembers(data, endpoint);
            const fallbackNext = readNext(data, endpoint);
            if (pageMembers.length === 0) {
                throw new Error(
                    `Hydra pagination made no member progress for ${endpoint}: expected ${total} unique members, received ${allMembers.length}`,
                );
            }
            allMembers.push(...pageMembers);
            if (fallbackNext !== undefined) {
                normalizeNext(client, endpoint, fallbackNext, collectionParams, page + 1);
                if (allMembers.length === total) {
                    throw new Error(`Hydra pagination advertises more pages after reaching total for ${endpoint}`);
                }
            }
            pagesFetched += 1;
            page += 1;
            if (allMembers.length > total) {
                throw cardinalityError(endpoint, total, allMembers.length);
            }
        }
    }

    assertUniqueMembers(allMembers, endpoint);
    return allMembers;
};
